import './Home.scss';

import { useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router';

import { FamilyProfileSwitcher } from '@components/FamilyProfileSwitcher/FamilyProfileSwitcher';
import { NeedHelp } from '@components/NeedHelp/NeedHelp';
import { RerequisitionCallout } from '@components/RerequisitionCallout/RerequisitionCallout';
import { Sanitize } from '@components/Sanitize';
import { ROUTES } from '@constants/routes';
import type { Requisition } from '@customTypes/requisition';
import useApp from '@hooks/useApp';
import { useSelectedProfile } from '@hooks/useSelectedProfile';
import ContentContainer from '@layouts/container/ContentContainer';
import PageContainer from '@layouts/container/PageContainer';
import ArrowRightSvg from '@statics/images/arrow-right.svg?react';
import { fetchOpenRequisitions, fetchRerequisitionEligibility } from '@store/actions/patientPortal';
import { recordGoogleAnalyticsSend } from '@utils/helpers';

import { HomeOrderCard } from './components/HomeOrderCard';
import { HomeOrderRow } from './components/HomeOrderRow';
import { NoOrdersEmptyState } from './components/NoOrdersEmptyState';
import { buildHomeOrderEntries, sortHomeOrdersByOrderDate, splitHomeOrders } from './homeRanking';
import { getHomeOrderProgress } from './homeTimeline';
import { useCollatedReportAvailability } from './useCollatedReportAvailability';

const Home = () => {
  const { familyProfiles, userFirstName } = useApp();
  const { activeProfile, activeProfileKey, isViewingSelf, profiles, selectProfile, selectedIndividual } =
    useSelectedProfile(familyProfiles);
  const [requisitions, setRequisitions] = useState<Requisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isRerequisitionEligible, setIsRerequisitionEligible] = useState(false);
  // Bumped after a Cures Act release so the card picks up its new status.
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    recordGoogleAnalyticsSend('pageview', ROUTES.HOME, 'Landing on Orders Home');
  }, []);

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      setLoading(true);
      setLoadError(false);
      setRequisitions([]);
      setIsRerequisitionEligible(false);

      const eligibilityRequest = fetchRerequisitionEligibility(selectedIndividual)
        .then(response => response.is_eligible === true)
        .catch(() => false);

      try {
        const [response, isEligible] = await Promise.all([
          fetchOpenRequisitions(selectedIndividual),
          eligibilityRequest,
        ]);
        if (!isActive) return;
        setRequisitions(response.requisitions ?? []);
        setIsRerequisitionEligible(isEligible);
      } catch (error) {
        if (!isActive) return;
        console.error('Error fetching requisitions for Home:', error);
        setLoadError(true);
        setRequisitions([]);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [selectedIndividual, reloadCount]);

  const sortedOrders = useMemo(() => sortHomeOrdersByOrderDate(buildHomeOrderEntries(requisitions)), [requisitions]);

  const { activeOrders, orderHistory } = splitHomeOrders(sortedOrders);
  // Same reported-product check My Results uses. A canceled history row is not enough.
  const hasReportedResults = sortedOrders.some(entry => getHomeOrderProgress(entry.requisition).reported > 0);

  // Only fully reported orders can have a collated report, so probe no further than those shown.
  const downloadCandidates = useMemo(
    () =>
      [...activeOrders, ...orderHistory]
        .filter(entry => getHomeOrderProgress(entry.requisition).isFullyReported)
        .map(entry => entry.requisition.requisition_id),
    [activeOrders, orderHistory],
  );
  const downloadableIds = useCollatedReportAvailability(downloadCandidates);

  const showEmptyState = !loading && !loadError && activeOrders.length === 0;
  const resultsTo = selectedIndividual
    ? `${ROUTES.RESULTS}?individual=${encodeURIComponent(selectedIndividual)}`
    : ROUTES.RESULTS;

  const resultsPrompt = hasReportedResults ? (
    <div className='home-results-prompt' data-testid='homeResultsPrompt'>
      <div className='home-results-prompt__copy'>
        <h3 className='home-results-prompt__title'>Explore your results</h3>
        <p className='home-results-prompt__body'>
          See what your results mean, recommended next steps, and share them with your provider.
        </p>
      </div>
      <Link className='home-results-prompt__action' to={resultsTo}>
        Go to My Results
        <ArrowRightSvg aria-hidden='true' />
      </Link>
    </div>
  ) : null;

  return (
    <PageContainer>
      <ContentContainer containerClass='py-4 home-header'>
        <h1 className='text-primary'>
          Welcome, <Sanitize>{userFirstName}</Sanitize>
        </h1>
        <FamilyProfileSwitcher profiles={profiles} activeKey={activeProfileKey} onSelect={selectProfile} />
        {isRerequisitionEligible && <RerequisitionCallout individualId={selectedIndividual} />}
      </ContentContainer>

      <ContentContainer containerClass='page-content home-content mt-0' contentClass='d-flex flex-column gap-4'>
        {loadError && (
          <Alert variant='danger' className='mb-0'>
            <Alert.Heading as='h5'>Error loading your orders</Alert.Heading>
            <p className='mb-0'>We encountered an error while loading your orders. Please reload the page.</p>
          </Alert>
        )}

        {showEmptyState && <NoOrdersEmptyState />}

        {activeOrders.length > 0 && (
          <section className='home-section'>
            <h2 className='home-section__title'>{activeOrders.length > 1 ? 'Latest Orders' : 'Latest Order'}</h2>
            {activeOrders.map((entry, index) => (
              <HomeOrderCard
                key={entry.requisition.requisition_id}
                requisition={entry.requisition}
                defaultExpanded={index === 0}
                canDownload={downloadableIds.has(entry.requisition.requisition_id)}
                resultsTo={resultsTo}
                onCuresActReleased={() => setReloadCount(count => count + 1)}
              />
            ))}
          </section>
        )}

        {orderHistory.length > 0 && (
          <section className='home-section'>
            <h2 className='home-section__title'>Order History</h2>
            <div className='home-order-table'>
              {/* Column key only; each row reads on its own without it. */}
              <div className='home-order-table__head' aria-hidden='true'>
                <span>Order ID</span>
                <span>Order date</span>
                <span>Tests included</span>
                <span className='home-order-table__head-status'>Status</span>
              </div>
              <ul className='home-order-table__body'>
                {orderHistory.map(entry => (
                  <HomeOrderRow key={entry.requisition.requisition_id} entry={entry} />
                ))}
              </ul>
            </div>
            {resultsPrompt}
          </section>
        )}

        {orderHistory.length === 0 && resultsPrompt}

        {loading && (
          <div className='home-loading text-primary' data-testid='homeLoading'>
            <Spinner animation='border' />
          </div>
        )}

        <NeedHelp variant='band' />
      </ContentContainer>
    </PageContainer>
  );
};

export default Home;
